import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { RootLayout } from '@/components/layout/RootLayout'
import { GlassCard } from '@/components/ui/GlassCard'
import { GlassButton } from '@/components/ui/GlassButton'
import { GlassInput } from '@/components/ui/GlassInput'
import { api } from '@/lib/api'
import { channelEndpointsQueryOptions, channelEndpointProfilesQueryOptions } from '@/lib/queryOptions'
import { useT } from '@/i18n/provider'

export const Route = createFileRoute('/settings/channels')({
  loader: async ({ context: { queryClient } }) => {
    await Promise.all([
      queryClient.ensureQueryData(channelEndpointsQueryOptions()),
      queryClient.ensureQueryData(channelEndpointProfilesQueryOptions()),
    ])
  },
  component: ChannelsPage,
})

function ChannelsPage() {
  const t = useT()
  const qc = useQueryClient()
  const { data: endpoints = [] } = useQuery(channelEndpointsQueryOptions())
  const { data: profiles = [] } = useQuery(channelEndpointProfilesQueryOptions())
  const [name, setName] = useState('')
  const [channelType, setChannelType] = useState<'TELEGRAM' | 'WHATSAPP' | 'PHONE'>('TELEGRAM')
  const [channelProfileId, setChannelProfileId] = useState('')
  const [configJson, setConfigJson] = useState('{}')
  const [error, setError] = useState('')

  const createMutation = useMutation({
    mutationFn: api.channels.endpoints.create,
    onSuccess: () => {
      setName('')
      setChannelType('TELEGRAM')
      setChannelProfileId('')
      setConfigJson('{}')
      setError('')
      qc.invalidateQueries({ queryKey: ['channels', 'endpoints'] })
    },
    onError: (err) => setError(err instanceof Error ? err.message : 'Failed'),
  })

  return (
    <RootLayout>
      <div className="space-y-6 max-w-3xl">
        <div>
          <h1 className="text-xl font-bold text-white">{t('settings.channels.title')}</h1>
          <p className="text-sm text-white/40 mt-0.5">{t('settings.channels.subtitle')}</p>
        </div>

        <GlassCard className="space-y-4">
          <h2 className="font-semibold text-white text-sm">{t('settings.channels.createTitle')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <GlassInput
              label={t('settings.channels.nameLabel')}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('settings.channels.namePlaceholder')}
            />
            <div>
              <label className="text-sm font-medium text-white/70 block mb-1.5">{t('settings.channels.typeLabel')}</label>
              <select
                value={channelType}
                onChange={(e) => setChannelType(e.target.value as 'TELEGRAM' | 'WHATSAPP' | 'PHONE')}
                className="glass w-full px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
              >
                <option value="TELEGRAM" style={{ background: '#1a1a2e' }}>Telegram</option>
                <option value="WHATSAPP" style={{ background: '#1a1a2e' }}>WhatsApp</option>
                <option value="PHONE" style={{ background: '#1a1a2e' }}>Phone</option>
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-white/70 block mb-1.5">{t('settings.channels.profileLabel')}</label>
              <select
                value={channelProfileId}
                onChange={(e) => setChannelProfileId(e.target.value)}
                className="glass w-full px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
              >
                <option value="">{t('settings.channels.profilePlaceholder')}</option>
                {profiles
                  .filter((profile) => profile.channelType === channelType)
                  .map((profile) => (
                    <option key={profile.id} value={profile.id} style={{ background: '#1a1a2e' }}>
                      {profile.name} ({profile.channelType})
                    </option>
                  ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-white/70 block mb-1.5">{t('settings.channels.configLabel')}</label>
              <textarea
                value={configJson}
                onChange={(e) => setConfigJson(e.target.value)}
                rows={4}
                className="glass w-full px-4 py-2.5 text-white text-sm bg-transparent focus:outline-none focus:ring-2 focus:ring-[var(--oc-accent)]/60"
              />
            </div>
          </div>
          {error && <p className="text-red-400 text-sm">{error}</p>}
          <GlassButton
            onClick={() => {
              try {
                const parsed = JSON.parse(configJson || '{}') as Record<string, unknown>
                createMutation.mutate({
                  name,
                  channelType,
                  channelProfileId: channelProfileId || undefined,
                  config: parsed,
                  status: 'ACTIVE',
                })
              } catch {
                setError(t('settings.channels.invalidJson'))
              }
            }}
            loading={createMutation.isPending}
            disabled={!name}
          >
            {t('settings.channels.createButton')}
          </GlassButton>
        </GlassCard>

        <div className="space-y-3">
          {endpoints.map((endpoint) => (
            <GlassCard key={endpoint.id} size="sm" className="space-y-2">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-white">{endpoint.name}</p>
                <p className="text-xs text-white/40">{endpoint.channelType} · {endpoint.status}</p>
              </div>
              <p className="text-xs text-white/35 font-mono break-all">
                {window.location.origin}/api/channels/{endpoint.channelType.toLowerCase() === 'phone' ? 'voice/twilio/inbound' : `${endpoint.channelType.toLowerCase()}/webhook`}/{endpoint.id}
              </p>
            </GlassCard>
          ))}
        </div>
      </div>
    </RootLayout>
  )
}
